/**
 * conversationService.ts
 * ─────────────────────────────────────────────────────────────
 * All Supabase read/write for conversations and messages.
 * Called from the store and useChat hook.
 * ─────────────────────────────────────────────────────────────
 */
import { getSupabase } from '../hooks/useAuth'
import type { Conversation, Message, SineModel, StudioConversationState } from '../types'

// ── Row types that match the Supabase schema ─────────────────
interface DbConversation {
  id: string
  user_id: string
  title: string
  mode: string
  agent_type: string | null
  app_mode?: string | null
  model?: string | null
  created_at: string
  updated_at: string
  is_favorite?: boolean
  credits_used?: number
  project_id?: string | null
  studio_state?: StudioConversationState | null
}

interface DbMessage {
  id: string
  conversation_id: string
  role: string
  content: string
  metadata: Record<string, unknown>
  created_at: string
}

function tryParseRawAgentPayload(content: string): Record<string, unknown> | null {
  const trimmed = content.trim()
  if (!trimmed.startsWith('{') || !trimmed.includes('agentStatus')) return null
  try {
    const parsed = JSON.parse(trimmed)
    if (parsed && typeof parsed === 'object' && ('agentStatus' in parsed || 'agentFiles' in parsed || 'agentTasks' in parsed)) {
      return parsed as Record<string, unknown>
    }
  } catch {
    return null
  }
  return null
}

// ── Mappers ──────────────────────────────────────────────────
function dbConvToLocal(row: DbConversation, messages: Message[] = []): Conversation {
  return {
    id: row.id,
    title: row.title,
    messages,
    createdAt: new Date(row.created_at),
    updatedAt: new Date(row.updated_at),
    model: ((row.model as SineModel) ?? 'sine-lite'),
    type: (row.mode as 'chat' | 'agent') ?? 'chat',
    agentType: (row.agent_type as 'code' | 'writing') ?? undefined,
    isFavorite: row.is_favorite ?? false,
    creditsUsed: row.credits_used ?? 0,
    projectId: row.project_id ?? null,
    appMode: (row.app_mode === 'studio' ? 'studio' : 'chat') as 'chat' | 'studio',
    useAgentMode: row.app_mode === 'agent',
    studioState: row.studio_state ?? null,
  }
}

function dbMsgToLocal(row: DbMessage): Message {
  const meta = row.metadata ?? {}
  const rawAgentPayload = tryParseRawAgentPayload(row.content)
  const isAgentMessage = (meta.isAgentMessage as boolean) ?? Boolean(rawAgentPayload)
  const hydratedContent = rawAgentPayload ? '' : row.content
  return {
    id: row.id,
    role: isAgentMessage ? 'agent' : (row.role as Message['role']),
    content: hydratedContent,
    createdAt: new Date(row.created_at),
    isStreaming: false,
    attachments: (meta.attachments as Message['attachments']) ?? undefined,
    agentTasks: (meta.agentTasks as Message['agentTasks']) ?? (rawAgentPayload?.agentTasks as Message['agentTasks']) ?? undefined,
    agentFiles: (meta.agentFiles as Message['agentFiles']) ?? (rawAgentPayload?.agentFiles as Message['agentFiles']) ?? undefined,
    agentStatus: (meta.agentStatus as Message['agentStatus']) ?? (rawAgentPayload?.agentStatus as Message['agentStatus']) ?? undefined,
    agentSuggestions: (meta.agentSuggestions as Message['agentSuggestions']) ?? undefined,
    agentSourcePack: (meta.agentSourcePack as Message['agentSourcePack']) ?? undefined,
    agentRecurringProposal: (meta.agentRecurringProposal as Message['agentRecurringProposal']) ?? undefined,
    agentIntentDecision: (meta.agentIntentDecision as Message['agentIntentDecision']) ?? undefined,
    agentSelectedCapabilities: (meta.agentSelectedCapabilities as Message['agentSelectedCapabilities']) ?? undefined,
    agentStructuredOutput: (meta.agentStructuredOutput as Message['agentStructuredOutput']) ?? undefined,
    agentMemorySummary: (meta.agentMemorySummary as Message['agentMemorySummary']) ?? undefined,
    agentWorkflowSummary: (meta.agentWorkflowSummary as Message['agentWorkflowSummary']) ?? undefined,
    agentSemanticEvents: (meta.agentSemanticEvents as Message['agentSemanticEvents']) ?? undefined,
    agentSubagentRuntime: (meta.agentSubagentRuntime as Message['agentSubagentRuntime']) ?? undefined,
    agentResponseMode: (meta.agentResponseMode as Message['agentResponseMode']) ?? undefined,
    agentPlan: meta.agentPlan ? {
      ...(meta.agentPlan as Message['agentPlan']),
      phases: ((meta.agentPlan as Message['agentPlan'])?.phases ?? []),
    } : undefined,
    agentPlanningTasks: (meta.agentPlanningTasks as Message['agentPlanningTasks']) ?? undefined,
    agentMessages: (meta.agentMessages as Message['agentMessages']) ?? undefined,
    agentEvents: (meta.agentEvents as Message['agentEvents']) ?? undefined,
    agentRunId: (meta.agentRunId as Message['agentRunId']) ?? undefined,
    isAgentMessage: isAgentMessage || undefined,
    askUserInput: (meta.askUserInput as Message['askUserInput']) ?? undefined,
    toolData: (meta.toolData as Message['toolData']) ?? undefined,
    agentWebdev: (meta.agentWebdev as Message['agentWebdev']) ?? undefined,
    agentBlueprint: (meta.agentBlueprint as Message['agentBlueprint']) ?? undefined,
    agentSlides: (meta.agentSlides as Message['agentSlides']) ?? undefined,
    agentPage: (meta.agentPage as Message['agentPage']) ?? undefined,
    imageGeneration: (meta.imageGeneration as Message['imageGeneration']) ?? undefined,
    modelProfile: (meta.modelProfile as Message['modelProfile']) ?? undefined,
    agentResearch: (meta.agentResearch as Message['agentResearch']) ?? undefined,
    agentMetrics: (meta.agentMetrics as Message['agentMetrics']) ?? undefined,
    agentBuilderHandoff: (meta.agentBuilderHandoff as Message['agentBuilderHandoff']) ?? undefined,
  }
}

function messageRolePriority(role: Message['role']): number {
  switch (role) {
    case 'user':
      return 0
    case 'assistant':
      return 1
    case 'agent':
      return 2
    default:
      return 3
  }
}

function sortMessagesChronologically(messages: Message[]): Message[] {
  return [...messages].sort((left, right) => {
    const timeDiff = left.createdAt.getTime() - right.createdAt.getTime()
    if (timeDiff !== 0) return timeDiff

    const roleDiff = messageRolePriority(left.role) - messageRolePriority(right.role)
    if (roleDiff !== 0) return roleDiff

    return left.id.localeCompare(right.id)
  })
}

function isTransientAgentFailure(message: Message): boolean {
  if (!message.isAgentMessage && message.role !== 'agent') return false
  if (message.agentStatus !== 'failed') return false
  if ((message.agentTasks?.length ?? 0) > 0) return false
  if ((message.agentFiles?.length ?? 0) > 0) return false
  const content = message.content.trim()
  return content === '' || /401|unauthorized|api-feil:\s*401/i.test(content)
}

function collapseDuplicateRetryMessages(messages: Message[]): Message[] {
  if (messages.length < 3) return messages
  const collapsed: Message[] = []

  for (let index = 0; index < messages.length; index += 1) {
    const current = messages[index]
    const next = messages[index + 1]
    const afterNext = messages[index + 2]

    if (
      current?.role === 'user' &&
      next &&
      isTransientAgentFailure(next) &&
      afterNext?.role === 'user' &&
      current.content.trim() !== '' &&
      current.content.trim() === afterNext.content.trim()
    ) {
      index += 1
      continue
    }

    collapsed.push(current)
  }

  return collapsed
}

export function buildMessageMetadata(message: Message): Record<string, unknown> {
  const metadata: Record<string, unknown> = {}
  if (message.attachments) metadata.attachments = message.attachments
  if (message.agentTasks) metadata.agentTasks = message.agentTasks
  if (message.agentFiles) metadata.agentFiles = message.agentFiles
  if (message.agentStatus) metadata.agentStatus = message.agentStatus
  if (message.agentSuggestions) metadata.agentSuggestions = message.agentSuggestions
  if (message.agentSourcePack) metadata.agentSourcePack = message.agentSourcePack
  if (message.agentRecurringProposal) metadata.agentRecurringProposal = message.agentRecurringProposal
  if (message.agentIntentDecision) metadata.agentIntentDecision = message.agentIntentDecision
  if (message.agentSelectedCapabilities) metadata.agentSelectedCapabilities = message.agentSelectedCapabilities
  if (message.agentStructuredOutput) metadata.agentStructuredOutput = message.agentStructuredOutput
  if (message.agentMemorySummary) metadata.agentMemorySummary = message.agentMemorySummary
  if (message.agentWorkflowSummary) metadata.agentWorkflowSummary = message.agentWorkflowSummary
  if (message.agentSemanticEvents) metadata.agentSemanticEvents = message.agentSemanticEvents
  if (message.agentSubagentRuntime) metadata.agentSubagentRuntime = message.agentSubagentRuntime
  if (message.agentResponseMode) metadata.agentResponseMode = message.agentResponseMode
  if (message.agentPlan) metadata.agentPlan = message.agentPlan
  if (message.agentPlanningTasks) metadata.agentPlanningTasks = message.agentPlanningTasks
  if (message.agentMessages) metadata.agentMessages = message.agentMessages
  if (message.agentEvents) metadata.agentEvents = message.agentEvents
  if (message.agentRunId) metadata.agentRunId = message.agentRunId
  if (message.isAgentMessage || message.role === 'agent') metadata.isAgentMessage = true
  if (message.askUserInput) metadata.askUserInput = message.askUserInput
  if (message.toolData) metadata.toolData = message.toolData
  if (message.agentWebdev) metadata.agentWebdev = message.agentWebdev
  if (message.agentBlueprint) metadata.agentBlueprint = message.agentBlueprint
  if (message.agentSlides) metadata.agentSlides = message.agentSlides
  if (message.agentPage) metadata.agentPage = message.agentPage
  if (message.imageGeneration) metadata.imageGeneration = message.imageGeneration
  if (message.modelProfile) metadata.modelProfile = message.modelProfile
  if (message.agentResearch) metadata.agentResearch = message.agentResearch
  if (message.agentMetrics) metadata.agentMetrics = message.agentMetrics
  if (message.agentBuilderHandoff) metadata.agentBuilderHandoff = message.agentBuilderHandoff
  return metadata
}

function normalizeDbRole(role: Message['role'] | string, metadata?: Record<string, unknown>): string {
  if (role === 'agent' || metadata?.isAgentMessage) return 'assistant'
  return role
}

// Explicit columns for conversations — never select(*)
const CONV_COLUMNS = 'id,user_id,title,mode,agent_type,app_mode,model,created_at,updated_at,is_favorite,credits_used,project_id,studio_state'
// Explicit columns for messages — never select(*)
const MSG_COLUMNS = 'id,conversation_id,role,content,metadata,created_at'

// ── Fetch paginated conversations (no messages — lazy loaded) ──
// Returns the N most-recently-updated conversations without any messages.
// Messages are fetched on demand via fetchMessagesForConversation.
export const CONVERSATIONS_PAGE_SIZE = 50

export async function fetchConversations(
  userId: string,
  page = 0,
): Promise<Conversation[]> {
  const supabase = getSupabase()
  const from = page * CONVERSATIONS_PAGE_SIZE
  const to = from + CONVERSATIONS_PAGE_SIZE - 1

  const { data: convRows, error: convErr } = await supabase
    .from('conversations')
    .select(CONV_COLUMNS)
    .eq('user_id', userId)
    .order('updated_at', { ascending: false })
    .range(from, to)

  if (convErr || !convRows) return []

  // Return conversations WITHOUT messages — messages are lazy-loaded
  return convRows.map(row => dbConvToLocal(row as DbConversation, []))
}

/** Max messages to load initially. Older messages are loaded on scroll via fetchOlderMessages(). */
export const MESSAGES_INITIAL_LIMIT = 50

// ── Fetch recent messages for a single conversation (paginated) ──
// Returns the most recent MESSAGES_INITIAL_LIMIT messages, sorted oldest-first.
export async function fetchMessagesForConversation(
  conversationId: string,
  limit = MESSAGES_INITIAL_LIMIT,
): Promise<Message[]> {
  const supabase = getSupabase()
  // Fetch newest N messages descending, then reverse for chronological display
  const { data: msgRows, error } = await supabase
    .from('messages')
    .select(MSG_COLUMNS)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error || !msgRows) return []
  // Reverse so oldest is first (chronological order for display)
  return sortMessagesChronologically(
    collapseDuplicateRetryMessages(msgRows.reverse().map(row => dbMsgToLocal(row as DbMessage)))
  )
}

// ── Fetch older messages before a given timestamp (for scroll-up pagination) ──
export async function fetchOlderMessages(
  conversationId: string,
  beforeCreatedAt: string,
  limit = MESSAGES_INITIAL_LIMIT,
): Promise<Message[]> {
  const supabase = getSupabase()
  const { data: msgRows, error } = await supabase
    .from('messages')
    .select(MSG_COLUMNS)
    .eq('conversation_id', conversationId)
    .lt('created_at', beforeCreatedAt)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (error || !msgRows) return []
  return sortMessagesChronologically(
    collapseDuplicateRetryMessages(msgRows.reverse().map(row => dbMsgToLocal(row as DbMessage)))
  )
}

// ── Create a new conversation row ────────────────────────────
export async function createConversationInDb(
  userId: string,
  id: string,
  title: string,
  mode: 'chat' | 'agent' = 'chat',
  agentType?: 'code' | 'writing',
  appMode?: 'chat' | 'studio',
  model?: string
): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').insert({
    id,
    user_id: userId,
    title,
    mode,
    agent_type: agentType ?? null,
    app_mode: appMode ?? 'chat',
    model: model ?? 'sine-lite',
  })
}

// ── Update model for a conversation ─────────────────────────────────
export async function updateConversationModelInDb(id: string, model: string): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ model }).eq('id', id)
}

// ── Update app_mode for a conversation ─────────────────────────────
// Accepts 'chat' | 'studio' | 'agent' — 'agent' persists useAgentMode
export async function updateConversationAppMode(id: string, appMode: 'chat' | 'studio' | 'agent'): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ app_mode: appMode }).eq('id', id)
}

export async function updateConversationStudioState(
  id: string,
  studioState: StudioConversationState | null,
): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ studio_state: studioState }).eq('id', id)
}

export async function updateConversationCreditsUsed(id: string, creditsUsed: number): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ credits_used: creditsUsed }).eq('id', id)
}

// ── Update conversation title ─────────────────────────────────
export async function updateConversationTitle(id: string, title: string): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ title }).eq('id', id)
}

// ── Delete a conversation (cascades to messages via FK) ───────
export async function deleteConversationFromDb(id: string): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').delete().eq('id', id)
}

// ── Insert a single message ───────────────────────────────────
export async function insertMessage(
  conversationId: string,
  message: Message
): Promise<void> {
  const supabase = getSupabase()
  const metadata = buildMessageMetadata(message)

  await supabase.from('messages').insert({
    id: message.id,
    conversation_id: conversationId,
    role: normalizeDbRole(message.role, metadata),
    content: message.content,
    metadata,
    created_at: message.createdAt.toISOString(),
  })
}

// ── Update message content (after streaming finishes) ────────
// Uses upsert so assistant messages are always persisted even if the
// initial placeholder row was never inserted (isStreaming=true was skipped).
export async function updateMessageContent(
  messageId: string,
  content: string,
  extraMetadata?: Record<string, unknown>,
  // These are needed for upsert when the row doesn't exist yet
  fallback?: { conversationId: string; role: string; createdAt: Date }
): Promise<void> {
  const supabase = getSupabase()

  if (fallback) {
    // Upsert: insert if not exists, update if exists
    const row: Record<string, unknown> = {
      id: messageId,
      conversation_id: fallback.conversationId,
      role: normalizeDbRole(fallback.role, extraMetadata),
      content,
      metadata: extraMetadata ?? {},
      created_at: fallback.createdAt.toISOString(),
    }
    await supabase.from('messages').upsert(row, { onConflict: 'id' })
  } else {
    const update: Record<string, unknown> = { content }
    if (extraMetadata) update.metadata = extraMetadata
    await supabase.from('messages').update(update).eq('id', messageId)
  }
}

// ── Toggle favourite ──────────────────────────────────────────
export async function toggleFavoriteInDb(id: string, isFavorite: boolean): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ is_favorite: isFavorite }).eq('id', id)
}

// ── Rename conversation ───────────────────────────────────────
// (alias kept here for clarity — same as updateConversationTitle)
export { updateConversationTitle as renameConversationInDb }

// ── Move conversation to project ────────────────────────────
export async function moveConversationToProject(
  conversationId: string,
  projectId: string | null
): Promise<void> {
  const supabase = getSupabase()
  await supabase
    .from('conversations')
    .update({ project_id: projectId })
    .eq('id', conversationId)
}

// ── Generate shareable link ───────────────────────────────────
export function buildShareLink(conversationId: string): string {
  return `${window.location.origin}/chat/${conversationId}`
}

// ── Mark conversation as public for sharing ────────────────
export async function markConversationPublic(conversationId: string): Promise<void> {
  const supabase = getSupabase()
  await supabase.from('conversations').update({ is_public: true }).eq('id', conversationId)
}
